import React, { useEffect, useState } from "react";

import { FloatButton } from "antd";
import Header from "../../components/header";
import Footer from "../../components/footer";
import FAQ from "../../components/faq";
import CouponRecommendations from "../../components/couponrecommendations";
import { useSearchParams } from "react-router-dom";
import { getCouponAPI, Offer } from "../../utils/API";
import AdPage from "../../components/AdPage";
import HelmetSeo from "../../components/HelmetSeo";
import {
  getPartnerBoostBrandById,
  PARTNERBOOST_SOURCE,
} from "../../utils/partnerBoost";
import { getBrandSeoMeta, getPartnerBoostSeoMeta } from "../../utils/seo";

const CopyDiscountCode: React.FC = () => {
  const [isShow, setIsShow] = useState(false);
  const [searchParams] = useSearchParams();
  const offer_id = searchParams.get("offer_id");
  const source = searchParams.get("source");
  const brandType = searchParams.get("brand_type");
  const [copyDiscountCodeData, setCopyDiscountCodeData] =
    useState<Offer | null>(null);

  const [couponRecommendationsData, setCouponRecommendationsData] = useState<
    Offer[]
  >([]);

  // 处理品牌logo URL
  const getBrandLogoUrl = (brandLogo: string | undefined) => {
    if (!brandLogo) return "/images/white.png";
    return brandLogo.replace("[size]", "200").replace("[format]", "png");
  };
  const clamp = (s: string, n: number) =>
    s.length > n ? `${s.slice(0, n)}…` : s;

  // 复制优惠码到剪贴板
  const handleCopyCode = async () => {
    const code =
      copyDiscountCodeData?.code ||
      (copyDiscountCodeData?.source === PARTNERBOOST_SOURCE
        ? copyDiscountCodeData?.affiliate_link || copyDiscountCodeData?.url
        : "");
    if (!code) return;

    try {
      // 使用 Clipboard API 复制文本
      await navigator.clipboard.writeText(code);
      // 显示对话框
      setIsShow(true);
    } catch (error) {
      // 如果 Clipboard API 不可用，使用传统方法
      const textArea = document.createElement("textarea");
      textArea.value = code;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setIsShow(true);
      } catch (err) {
        console.error("Failed to copy code:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    const fetchPageData = async () => {
      if (source === PARTNERBOOST_SOURCE && offer_id) {
        const { result, brand, relatedBrands } = await getPartnerBoostBrandById(
          offer_id,
          brandType,
        );
        if (!result || !brand) {
          setCopyDiscountCodeData(null);
          setCouponRecommendationsData([]);
          return;
        }

        setCopyDiscountCodeData({
          ...brand,
          title: brand.merchant_name || brand.title,
          code: brand.label || brand.code,
          url: brand.affiliate_link || brand.url,
        });
        setCouponRecommendationsData(relatedBrands.slice(0, 20));
        return;
      }

      const { result, offers } = await getCouponAPI();
      if (!result) return;

      const offerData = offers.find((offer) => offer.offer_id === offer_id);
      setCopyDiscountCodeData(offerData || null);

      const randomData = [...offers].sort(() => 0.5 - Math.random());
      const seenStores = new Set<string>();
      const uniqueByStore: Offer[] = [];
      for (const offer of randomData) {
        const storeKey = offer.store?.trim().toLowerCase();
        if (!storeKey) continue;
        if (seenStores.has(storeKey)) continue;
        seenStores.add(storeKey);
        uniqueByStore.push(offer);
        if (uniqueByStore.length === 20) break;
      }
      setCouponRecommendationsData(uniqueByStore);
    };

    fetchPageData();
  }, [offer_id, source, brandType]);

  const isPartnerBoost =
    source === PARTNERBOOST_SOURCE ||
    copyDiscountCodeData?.source === PARTNERBOOST_SOURCE;
  const seoMeta = isPartnerBoost
    ? getPartnerBoostSeoMeta(
        copyDiscountCodeData?.merchant_name || copyDiscountCodeData?.title,
        copyDiscountCodeData?.description,
      )
    : getBrandSeoMeta(
        copyDiscountCodeData?.store,
        copyDiscountCodeData?.merchant_name || copyDiscountCodeData?.title,
        copyDiscountCodeData?.description,
      );

  return (
    <>
      <HelmetSeo title={seoMeta.title} description={seoMeta.description} />
      <div className="relative w-full h-full flex flex-col justify-between bg-red-500">
        <Header />

        <AdPage />

        <main className="flex-1 w-full max-w-[1300px] mx-auto bg-red-500 h-auto">
          <div className="h-auto mx-auto">
            <div className="flex items-center justify-center">
              <div className="absolute z-0 top-[56px] md:top-[70px] w-full max-w-[1200px] mx-auto min-h-[265px] h-[244px] md:h-[400px]">
                <img
                  src="/images/coupon-home-pc.png"
                  alt=""
                  className="w-full h-full object-contain opacity-70"
                  loading="lazy"
                  decoding="async"
                  fetch-priority="auto"
                  referrerPolicy="no-referrer"
                  crossOrigin="anonymous"
                  width="100%"
                  height="100%"
                />
              </div>
            </div>

            <div className="w-full relative z-1 mt-[120px] md:mt-[280px] h-auto px-4">
              <div className="w-full relative z-1 mt-[70px] md:mt-[280px] h-auto">
                <div className="flex gap-[60px] max-w-[1200px] mx-auto px-3.5 h-auto pb-16">
                  <div className="w-full flex flex-col justify-center items-center gap-6 md:gap-[30px] h-auto">
                    <div className="rounded-[10px] md:rounded-[15px] border md:border-[1.5] border-[#ffffff1a] bg-white">
                      <img
                        src={getBrandLogoUrl(copyDiscountCodeData?.brand_logo)}
                        alt={
                          copyDiscountCodeData?.merchant_name ||
                          copyDiscountCodeData?.title ||
                          "brand logo"
                        }
                        loading="lazy"
                        decoding="async"
                        width="74"
                        height="74"
                        onError={(e) => {
                          const target = e.currentTarget;
                          if (target.src.endsWith("/images/white.png")) return;
                          target.src = "/images/white.png";
                        }}
                        className="w-28 h-28 px-1 md:w-40 md:h-40 rounded-[10px] md:rounded-[20px] object-contain transition-all hover:shadow-lg"
                      />
                    </div>
                    <h1 className="-mt-3 md:mt-0 font-bold text-xl leading-6 text-white text-center md:text-[32px] md:leading-10 line-clamp-2">
                      {copyDiscountCodeData?.title}
                    </h1>

                    <div className="min-w-[324px] w-[324px] md:min-w-[540px] flex mx-auto rounded-md border border-[#fb7401] bg-[#f7f7f7]">
                      <div className="flex-1 h-10 md:h-[68px] py-2.5 md:py-[19px] px-6 text-base md:text-2xl font-bold leading-5 md:leading-[30px] text-left text-[#130300] truncate">
                        {copyDiscountCodeData?.code}
                      </div>
                      <div
                        className="md:hidden cursor-pointer min-w-[160px] w-[160px] md:w-[210px] md:min-w-[210px] h-10 md:h-[68px] rounded-r-md bg-[#ffbc0f] hover:bg-primary px-3 py-2.5 md:p-5 text-[#130300] font-bold text-base md:text-2xl leading-5 md:leading-[30px] text-center"
                        onClick={handleCopyCode}
                      >
                        Copy Code
                      </div>
                      <button
                        className="hidden md:block cursor-pointer min-w-[160px] w-[160px] md:w-[210px] md:min-w-[210px] h-10 md:h-[68px] rounded-r-md bg-[#ffbc0f] hover:bg-primary px-3 py-2.5 md:p-5 text-[#130300] font-bold text-base md:text-2xl leading-5 md:leading-[30px] text-center"
                        type="button"
                        aria-haspopup="dialog"
                        aria-expanded="false"
                        aria-controls="radix-:r7R1:"
                        data-state="closed"
                        onClick={handleCopyCode}
                      >
                        Copy Code
                      </button>
                    </div>
                    {/* -------------- */}
                    {isShow && (
                      <>
                        <div
                          data-state="open"
                          className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                          style={{ pointerEvents: "auto" }}
                          data-aria-hidden="true"
                          aria-hidden="true"
                        ></div>
                        <div
                          role="dialog"
                          id="radix-:r7R1:"
                          aria-describedby="radix-:r7R1H2:"
                          aria-labelledby="radix-:r7R1H1:"
                          data-state="open"
                          className="fixed left-[50%] top-[50%] z-50 w-[90%] max-w-lg md:min-w-[700px] lg:max-w-[900px] xl:max-w-[1028px] p-6 translate-x-[-50%] translate-y-[-50%] gap-4 border shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg md:block bg-[#fffffff3]"
                          tabIndex={-1}
                          style={{
                            pointerEvents: "auto",
                            height: "auto !important",
                          }}
                        >
                          <div
                            className="flex flex-col space-y-1.5 text-center sm:text-left"
                            style={{ height: "auto !important" }}
                          >
                            <div
                              id="radix-:r7R1H2:"
                              className="text-sm text-muted-foreground"
                              style={{ height: "auto !important" }}
                            >
                              <div
                                className="flex justify-center items-center flex-col gap-10 py-10"
                                style={{ height: "auto !important" }}
                              >
                                <div className="min-w-[243px] border text-center rounded-md bg-[#ffbc0f] py-[11px] px-[60px] font-bold text-2xl leading-10 text-[#130300]">
                                  {copyDiscountCodeData?.code}
                                </div>
                                <a
                                  href={copyDiscountCodeData?.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-black text-white text-2xl leading-10 rounded-md py-[12px] px-4 md:px-16 bg-[#ef312b] text-center hover:bg-[#ee4e2e] transition-all duration-300 cursor-pointer"
                                >
                                  {clamp(copyDiscountCodeData?.url || "", 24)}
                                </a>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                            onClick={() => {
                              setIsShow(false);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-x h-4 w-4"
                              aria-hidden="true"
                            >
                              <path d="M18 6 6 18"></path>
                              <path d="m6 6 12 12"></path>
                            </svg>
                            <span className="sr-only">Close</span>
                          </button>
                        </div>
                      </>
                    )}
                    {/* -------------- */}
                  </div>
                </div>
                <div className="max-w-[1200px] mx-auto flex justify-center items-center md:justify-start gap-[2px] mt-4 md:mt-16 mb-5 md:mb-8 px-3.5">
                  <img
                    src="/icons/hot.png"
                    alt="hot"
                    loading="lazy"
                    decoding="async"
                    fetch-priority="auto"
                    width="28"
                    height="28"
                    className="w-[28px] h-[28px] md:w-[36px] md:h-[36px] object-contain"
                  />
                  <span className="text-xl flex justify-center items-center md:text-[28px] md:leading-[42px] font-black text-white">
                    You may also like
                  </span>
                  <img
                    src="/icons/hot.png"
                    alt="hot"
                    loading="lazy"
                    decoding="async"
                    fetch-priority="auto"
                    width="28"
                    height="28"
                    className="w-[28px] h-[28px] md:w-[36px] md:h-[36px] object-contain"
                  />
                </div>
                <div className="max-w-[1300px] mx-auto">
                  <CouponRecommendations
                    brandListData={couponRecommendationsData}
                    detailLinkParams={
                      isPartnerBoost
                        ? {
                            source: PARTNERBOOST_SOURCE,
                            brandType: brandType || undefined,
                          }
                        : undefined
                    }
                  />
                </div>
                <FAQ />
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
      <FloatButton.BackTop />
    </>
  );
};

export default CopyDiscountCode;
